<tui-drawer *tuiPopup="open()">
  <div class="tui-scrollbar" #scrollContainer>
    <form [formGroup]="teamMembersForm" [style.max-width.rem]="35" tuiForm="m" (ngSubmit)="onSubmit()"
      (keypress)="onKeyPress($event)">

      <!-- Custom template for parking dropdown items -->
      <ng-template #parkingContent let-parking>
        {{ parking.name }}
      </ng-template>

      <header tuiHeader>
        <h3 tuiTitle>
          {{ editingMemberId ? 'Edit Team Member' : 'Invite Team Members' }}
          <span tuiSubtitle>
            {{ editingMemberId ? 'Update team member details and their access permissions.' : 'Invite colleagues to your
            organization and assign them roles based on what they need to access — from full admin control to parking
            operations.' }}
          </span>
        </h3>
        <button tuiIconButton tuiButtonClose type="button" (click)="closeDrawer()">
          Close
        </button>
      </header>

      <!-- API Error Message -->
      <div *ngIf="apiErrorMessage()" #errorNotification id="errorNotification">
        <tui-notification appearance="negative" icon="@tui.circle-alert" (close)="apiErrorMessage.set(null)">
          {{ apiErrorMessage() }}
          <button iconStart="@tui.x" tuiIconButton type="button" (click)="apiErrorMessage.set(null)">
            Close
          </button>
        </tui-notification>
      </div>


      <!-- User Access Selection (Visible in Org POV to allow switching to Parking Access) -->
      <div *ngIf="!isInParkingView()">
        <app-input-heading heading="User Access"></app-input-heading>
        <div [style.margin-top.rem]="1">
          <tui-textfield tuiChevron>
            <select tuiTextfield [ngModel]="selectedAccessType" (ngModelChange)="onAccessTypeChange($event)"
              [ngModelOptions]="{standalone: true}" [disabled]="isEditing">
              <option *ngFor="let item of accessTypes" [value]="item">
                {{ item }}
              </option>
            </select>
            <tui-data-list-wrapper *tuiTextfieldDropdown [items]="accessTypes" />
          </tui-textfield>
        </div>

        <!-- Multi-select for Parkings - only when Parking Access is selected -->
        <div *ngIf="selectedAccessType === 'Parking Access'" [style.margin-top.rem]="1">
          <tui-textfield multi tuiChevron [tuiTextfieldCleaner]="true" [stringify]="stringifyParking">
            <label tuiLabel>Select Parkings</label>
            <input tuiInputChip [formControl]="parkingFormControl" [placeholder]="'Choose parkings'" />
            <tui-input-chip *tuiItem>
              <ng-template let-item>
                {{ item.name }}
              </ng-template>
            </tui-input-chip>
            <tui-data-list *tuiTextfieldDropdown>
              <button *ngFor="let item of getFilteredParkingList()" tuiOption [value]="item">
                {{ item.name }}
              </button>
            </tui-data-list>
          </tui-textfield>
        </div>
      </div>

      <!-- Segmented Tabs (Optional, can be hidden if User Access dropdown is enough) -->
      <div *ngIf="shouldShowTabs() && false">
        <tui-segmented [size]="'s'" [style.width]="'fit-content'">
          <button type="button" [class.active]="activeTab() === 'organization'"
            (click)="onTabChange('organization')">Organization Access</button>
          <button type="button" [class.active]="activeTab() === 'parking'" (click)="onTabChange('parking')">Parking
            Access</button>
        </tui-segmented>
      </div>

      <!-- Organization Tab Content (show when tabs are enabled and organization tab is active, OR when tabs are disabled and NOT in parking view and NOT editing with parking) -->
      <div
        *ngIf="shouldShowTabs() ? activeTab() === 'organization' : (!isInParkingView() && !(editingMemberId && parkingFormControl.value))">
        <!-- Preview section - shows saved members data -->
        <div tuiCell class="flex-info px-1" *ngFor="let member of savedMembers; let i = index">
          <ng-container *ngIf="!(isEditing && editingIndex === i)">
            <tui-avatar [src]="member.nameValue | tuiInitials" size="m" />
            <div tuiAccessories tuiTitle [style.align-self]="'auto'">
              <span [style.font-weight]="'var(--medium)'">
                {{ member.nameValue }}
              </span>
              <div tuiSubtitle [style.color]="'var(--tui-text-secondary)'">
                {{ member | memberSubtitle : teamMembersForm.get('role')?.value : parkingFormControl.value }}
              </div>
            </div>
            <div tuiTitle>
              <button iconStart="@tui.pen-line" tuiIconButton type="button" size="s" appearance="flat"
                (click)="editMember(i)">Edit</button>
            </div>
          </ng-container>
        </div>

        <!-- Hidden form control to maintain parking_id when it's already set -->
        <div *ngIf="hasExistingParkingId()" style="display: none;">
          <input [formControl]="parkingFormControl" />
        </div>

        <div [style.margin-bottom.rem]="1">
          <app-input-heading heading="User Info"></app-input-heading>
        </div>

        <!-- Team Members Form Array -->
        <ng-container formArrayName="members">
          <ng-container *ngFor="let memberForm of teamMembers.controls; let i = index" [formGroupName]="i">
            <div style="display: flex; flex-direction: column; gap: .5rem;">
              <!-- Phone -->
              <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                <tui-textfield [tuiTextfieldCustomContent]="loader">
                  <label tuiLabel>Phone<span *ngIf="isParkingAccess() && !isEditing">*</span></label>
                  <input tuiInputPhoneInternational [countries]="countries" [tuiTextfieldLabelOutside]="true"
                    placeholder="Enter phone number" formControlName="phoneValue" [(countryIsoCode)]="countryIsoCode"
                    (keydown)="onPhoneKeyDown($event)" (input)="onPhoneInput($event, memberForm)"
                    (paste)="onPhonePaste($event, memberForm)" (focus)="onPhoneFocus()" (click)="onPhoneClick($event)"
                    #phoneInputRef
                    [attr.id]="'phone_input_' + i" class="custom-small-input" />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'phoneValue'"
                  [error]="[] | tuiFieldError | async"></tui-error>
              </div>

              <!-- Email -->
              <div class="email-input-wrapper">
                <tui-textfield [tuiTextfieldCustomContent]="loader">
                  <label tuiLabel>Email<span *ngIf="isOrganizationAccess() && !isEditing">*</span></label>
                  <input formControlName="emailValue" tuiTextfield class="custom-small-input"
                    [tuiTextfieldLabelOutside]="true" placeholder="Enter email address"
                    (input)="onEmailInput($event, memberForm)" (focus)="onEmailFocus(memberForm)" (blur)="onEmailBlur()"
                    [class.read-only-email]="isEditing" [readOnly]="isEditing" [attr.id]="'email_input_' + i" />
                </tui-textfield>

                <small class="input-des"
                  *ngIf="!(memberForm | memberFormControl : 'emailValue').invalid || !((memberForm | memberFormControl : 'emailValue').dirty || (memberForm | memberFormControl : 'emailValue').touched)">
                  This will be the email the user signs in.
                </small>
                <tui-error [formControl]="memberForm | memberFormControl : 'emailValue'"
                  [error]="[] | tuiFieldError | async" [style.margin-top.rem]="'.01'"></tui-error>
                <!-- Inline error: at-least-one-of email/phone (Parking Access) -->
                <small *ngIf="phoneEmailError() && isParkingAccess() && !isEditing"
                  style="color: var(--tui-status-negative, #f33); font-size: 0.75rem; margin-top: 0.25rem; display: block;">
                  {{ phoneEmailError() }}
                </small>
              </div>

              <!-- Password -->
              <div *ngIf="!isEditing">
                <tui-textfield [tuiTextfieldCustomContent]="generatePasswordTpl">
                  <label tuiLabel>Password<span *ngIf="!isEditing">*</span></label>
                  <input tuiPassword type="password" formControlName="passwordValue" tuiTextfield
                    class="custom-small-input" [tuiTextfieldLabelOutside]="true" placeholder="Create a password"
                    [attr.id]="'password_input_' + i" />
                  <tui-icon tuiPassword />
                  <ng-template #generatePasswordTpl>
                    <button appearance="secondary-grayscale" size="xs" tuiButton type="button"
                      (click)="generatePassword(i)" [style.margin-right.rem]="0.5" [style.border-radius.rem]="0.375"
                      class="generate-btn">Generate</button>
                  </ng-template>
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'passwordValue'"
                  [error]="[] | tuiFieldError | async" [style.margin-top.rem]="'.01'"></tui-error>
              </div>

              <!-- Name -->
              <div>
                <tui-textfield>
                  <label tuiLabel>Full Name</label>
                  <input formControlName="nameValue" tuiTextfield class="custom-small-input"
                    [tuiTextfieldLabelOutside]="true" placeholder="Enter full name" [attr.id]="'name_input_' + i" />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'nameValue'"
                  [error]="[] | tuiFieldError | async" [style.margin-top.rem]="'.01'"></tui-error>
              </div>
            </div>

            <!-- Divider between members -->
            <div *ngIf="i < teamMembers.length - 1" class="member-divider"
              style="margin-top: 1.5rem; margin-bottom: 1.5rem;"></div>
          </ng-container>
        </ng-container>


        <!-- Add Member button - saves current member and clears form -->
        <div *ngIf="stepType !== 'parking-manager' && !editingMemberId">
          <button iconStart="@tui.plus" tuiButton type="button" size="s" appearance="secondary"
            [disabled]="!teamMembers.at(0)?.valid" (click)="onAddAnother()">
            Add Another
          </button>
        </div>

        <!-- Hidden form control to maintain parking_id when it's already set (standard default) -->
        <div *ngIf="isInParkingView() && activeTab() !== 'organization'" style="display: none;">
          <input [formControl]="parkingFormControl" />
        </div>









        <!-- permissions list (shown when showPermissions is true and Parking Access is selected) -->
        <ng-container
          *ngIf="showPermissions && !isRoleAdminSelected() && stepType !== 'parking-manager' && isParkingAccess() && (parkingFormControl.value?.length > 0 || isInParkingView())">

          <div [style.margin-top.rem]="1" [style.margin-bottom.rem]="1">
            <app-input-heading heading="Choose Permissions"></app-input-heading>
          </div>

          <div class="permissions-list">
            <div class="card-container">
              <!-- Dropdown header -->
              <div *ngFor="let module of modulesData" class="permissions-list-item">
                <div class="flex-label-container" style="cursor: pointer;">
                  <label tuiLabel class="flex-label" (click)="$event.stopPropagation()">
                    <input size="m" tuiCheckbox type="checkbox"
                      [formControl]="modulesForm | mainModuleControl : module.id"
                      (change)="toggleModuleChildren(module)"
                      [indeterminate]="module | moduleIndeterminate : modulesForm.value : modulesForm" />
                    {{module.name}} <tui-badge>{{module | moduleSelectedCount : modulesForm.value :
                      modulesForm}}/{{module.childModules?.length ||
                      0}}</tui-badge>
                  </label>
                </div>

                <!-- Children always visible (accordion removed) -->
                <div class="grid" *ngIf="module.childModules && module.childModules.length > 0">
                  <div class="moduels-col" *ngFor="let child of (module.childModules || [])">
                    <label tuiLabel class="flex">
                      <input size="m" tuiCheckbox type="checkbox"
                        [formControl]="modulesForm | childModuleControl : module.id : child.id"
                        (change)="onChildModuleChange(module)" />
                      {{child.name}}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <div [style.margin-top.rem]="1" [style.margin-bottom.rem]=".5">
          <app-input-heading heading="Choose Role"></app-input-heading>
        </div>

        <!-- Loading state for roles -->
        <div *ngIf="loadingRoles()" class="role-loading">
          <tui-notification size="m" appearance="info" icon="@tui.loader">
            <b>Loading Roles...</b>
            <p>Please wait while we load available roles from the server.</p>
          </tui-notification>
        </div>

        <!-- Roles list -->
        <div *ngIf="!loadingRoles()" class="role-list">
          <div class="role-item" [class.role-item-error]="roleControl.invalid && roleControl.touched">
            <tui-radio-list formControlName="role" [items]="getFilteredRoleList()"
              [itemContent]="roleContent"></tui-radio-list>
            <tui-error [formControl]="roleControl" [error]="[] | tuiFieldError | async"
              [style.margin-top.rem]="0.0001" />
            <ng-template #roleContent let-item>
              <span tuiTitle>
                {{ item.name }}
                <span [style.font]="'var(--tui-font-text-ui-s)'" [style.color]="'var(--tui-text-secondary)'">{{
                  item.description }}</span>
              </span>
            </ng-template>
          </div>
        </div>

        <div [style.margin-top.rem]="1" [style.margin-bottom.rem]="1">
          <app-input-heading heading="Note"></app-input-heading>
        </div>
        <ul>
          <li>You can assign different roles to limit what each user can view or manage.</li>
          <li>Operators should be scoped to specific parking locations.</li>
          <li>Invitees will receive an email with instructions to join and set up their account.</li>
          <li>You can update roles and permissions later from the Users & Roles section.</li>
        </ul>
      </div>

      <!-- Parking Tab Content (show when tabs are enabled and parking tab is active, OR when tabs are disabled and in parking view OR editing with parking) -->
      <div
        *ngIf="shouldShowTabs() ? activeTab() === 'parking' : (isInParkingView() || (editingMemberId && parkingFormControl.value))">
        <!-- Preview section - shows saved members data -->
        <div tuiCell class="flex-info px-1" *ngFor="let member of savedMembers; let i = index">
          <ng-container *ngIf="!(isEditing && editingIndex === i)">
            <tui-avatar [src]="member.nameValue | tuiInitials" size="m" />
            <div tuiAccessories tuiTitle [style.align-self]="'auto'">
              <span [style.font-weight]="'var(--medium)'">
                {{ member.nameValue }}
              </span>
              <div tuiSubtitle [style.color]="'var(--tui-text-secondary)'">
                {{ member | memberSubtitle : teamMembersForm.get('role')?.value : parkingFormControl.value }}
              </div>
            </div>
            <div tuiTitle>
              <button iconStart="@tui.pen-line" tuiIconButton type="button" size="m" appearance="flat"
                (click)="editMember(i)">Edit</button>
            </div>
          </ng-container>
        </div>



        <!-- Hidden form control to maintain parking_id when it's already set -->
        <div *ngIf="hasExistingParkingId()" style="display: none;">
          <input [formControl]="parkingFormControl" />
        </div>

        <div [style.margin-bottom.rem]="1">
          <app-input-heading heading="User Info"></app-input-heading>
        </div>

        <!-- Team Members Form Array -->
        <ng-container formArrayName="members">
          <ng-container *ngFor="let memberForm of teamMembers.controls; let i = index" [formGroupName]="i">
            <div style="display: flex; flex-direction: column; gap: .5rem;">
              <!-- Phone -->
              <div>
                <tui-textfield>
                  <label tuiLabel>Phone</label>
                  <input tuiInputPhoneInternational [countries]="countries" [tuiTextfieldLabelOutside]="true"
                    placeholder="Enter phone number" formControlName="phoneValue" [(countryIsoCode)]="countryIsoCode"
                    (keydown)="onPhoneKeyDown($event)" (input)="onPhoneInput($event, memberForm)"
                    (paste)="onPhonePaste($event, memberForm)" (focus)="onPhoneFocus()" (click)="onPhoneClick($event)"
                    #phoneInputRef
                    [attr.id]="'parking_phone_input_' + i" class="custom-small-input" />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'phoneValue'"
                  [error]="[] | tuiFieldError | async"></tui-error>
              </div>

              <!-- Email -->
              <div>
                <tui-textfield>
                  <label tuiLabel *ngIf="!isEditing">Email</label>
                  <input formControlName="emailValue" tuiTextfield class="custom-small-input"
                    [tuiTextfieldLabelOutside]="true" placeholder="Enter email address"
                    (input)="onEmailInput($event, memberForm)" (focus)="onEmailFocus(memberForm)" (blur)="onEmailBlur()"
                    [attr.id]="'parking_email_input_' + i" />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'emailValue'"
                  [error]="[] | tuiFieldError | async"></tui-error>
                <!-- Inline error: at-least-one-of email/phone -->
                <small *ngIf="phoneEmailError() && !isEditing"
                  style="color: var(--tui-status-negative, #f33); font-size: 0.75rem; margin-top: 0.25rem; display: block;">
                  {{ phoneEmailError() }}
                </small>
              </div>

              <!-- Password -->
              <div *ngIf="!isEditing">
                <tui-textfield>
                  <label tuiLabel>Password<span *ngIf="!isEditing">*</span></label>
                  <input tuiPassword type="password" formControlName="passwordValue" tuiTextfield
                    class="custom-small-input" [tuiTextfieldLabelOutside]="true" placeholder="Create a password"
                    [attr.id]="'parking_password_input_' + i" />
                  <tui-icon tuiPassword />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'passwordValue'"
                  [error]="[] | tuiFieldError | async"></tui-error>
              </div>

              <!-- Name -->
              <div>
                <tui-textfield>
                  <label tuiLabel>Full Name</label>
                  <input formControlName="nameValue" tuiTextfield class="custom-small-input"
                    [tuiTextfieldLabelOutside]="true" placeholder="Enter full name"
                    [attr.id]="'parking_name_input_' + i" />
                </tui-textfield>
                <tui-error [formControl]="memberForm | memberFormControl : 'nameValue'"
                  [error]="[] | tuiFieldError | async"></tui-error>
              </div>
            </div>

            <!-- Divider between members -->
            <div *ngIf="i < teamMembers.length - 1" class="member-divider"
              style="margin-top: 1.5rem; margin-bottom: 1.5rem;"></div>
          </ng-container>
        </ng-container>

        <!-- Add Member button - saves current member and clears form -->
        <div *ngIf="stepType !== 'parking-manager' && !editingMemberId" [style.margin-top.rem]=".5"
          [style.margin-bottom.rem]="1">
          <button iconStart="@tui.plus" tuiButton type="button" size="s" appearance="secondary"
            [disabled]="!teamMembers.at(0)?.valid" (click)="onAddAnother()">
            Add Another
          </button>
        </div>


        <!-- permissions list (shown when showPermissions is true and a parking is selected or exists in context) -->
        <ng-container
          *ngIf="showPermissions && !isRoleAdminSelected() && stepType !== 'parking-manager' && (parkingFormControl.value || isInParkingView())">

          <div [style.margin-bottom.rem]="1" [style.margin-bottom.rem]="1">
            <app-input-heading heading="Choose Permissions"></app-input-heading>
          </div>

          <div class="permissions-list">
            <div class="card-container">
              <!-- Dropdown header -->
              <div *ngFor="let module of modulesData" class="permissions-list-item">
                <div class="flex-label-container" style="cursor: pointer;">
                  <label tuiLabel class="flex-label" (click)="$event.stopPropagation()">
                    <input size="m" tuiCheckbox type="checkbox"
                      [formControl]="modulesForm | mainModuleControl : module.id"
                      (change)="toggleModuleChildren(module)"
                      [indeterminate]="module | moduleIndeterminate : modulesForm.value : modulesForm" />
                    {{module.name}} <tui-badge>{{module | moduleSelectedCount : modulesForm.value :
                      modulesForm}}/{{module.childModules?.length ||
                      0}}</tui-badge>
                  </label>
                </div>

                <!-- Children always visible (accordion removed) -->
                <div class="grid" *ngIf="module.childModules && module.childModules.length > 0">
                  <div class="moduels-col" *ngFor="let child of (module.childModules || [])">
                    <label tuiLabel class="flex">
                      <input size="m" tuiCheckbox type="checkbox"
                        [formControl]="modulesForm | childModuleControl : module.id : child.id"
                        (change)="onChildModuleChange(module)" />
                      {{child.name}}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- Parking selection - Hidden dropdown but current parking is preserved -->
        <!-- Show for admin users OR when stepType is parking-manager -->
        <div *ngIf="isAdminUser() || stepType === 'parking-manager'" style="display: none;">
          <!-- Hidden form control to maintain current parking selection -->
          <input [formControl]="parkingFormControl" />
        </div>

        <div [style.margin-bottom.rem]="1">
          <app-input-heading heading="Choose Role"></app-input-heading>
        </div>

        <!-- Loading state for roles -->
        <div *ngIf="loadingRoles()" class="role-loading">
          <tui-notification size="m" appearance="info" icon="@tui.loader">
            <b>Loading Roles...</b>
            <p>Please wait while we load available roles from the server.</p>
          </tui-notification>
        </div>

        <!-- Roles list -->
        <div *ngIf="!loadingRoles()" class="role-list">
          <div class="role-item" [class.role-item-error]="roleControl.invalid && roleControl.touched">
            <tui-radio-list formControlName="role" [items]="getFilteredRoleList()"
              [itemContent]="roleContent"></tui-radio-list>
            <tui-error [formControl]="roleControl" [error]="[] | tuiFieldError | async"
              [style.margin-top.rem]="0.0001" />
            <ng-template #roleContent let-item>
              <span tuiTitle>
                {{ item.name }}
                <span [style.font]="'var(--tui-font-text-ui-s)'" [style.color]="'var(--tui-text-secondary)'">{{
                  item.description }}</span>
              </span>
            </ng-template>
          </div>
        </div>

        <div [style.margin-top.rem]="1" [style.margin-bottom.rem]="1">
          <app-input-heading heading="Note"></app-input-heading>
        </div>
        <ul>
          <li>You can assign different roles to limit what each user can view or manage.</li>
          <li>Operators should be scoped to specific parking locations.</li>
          <li>Invitees will receive an email with instructions to join and set up their account.</li>
          <li>You can update roles and permissions later from the Users & Roles section.</li>
        </ul>
      </div>
    </form>
  </div>
  <footer style="display: flex; justify-content: flex-end; gap: 1rem; padding: 1rem;">
    <button appearance="text" size="m" tuiButton type="button" (click)="closeDrawer()">Cancel</button>
    <button appearance="primary" size="m" tuiButton type="button" [appPermissionDisable]="'Team & Access Control'"
      [appPermissionAction]="editingMemberId ? 'update' : 'create'" [appPermissionSubModule]="'Team'"
      [disabled]="submissionStatus() === 'submitting'"
      [iconStart]="submissionStatus() === 'submitting' ? '@tui.loader' : undefined" (click)="onSubmit()">
      <span *ngIf="submissionStatus() === 'submitting'">Saving...</span>
      <span *ngIf="submissionStatus() === 'success'">Submitted!</span>
      <span *ngIf="submissionStatus() !== 'success' && submissionStatus() !== 'submitting'">
        {{ editingMemberId ? 'Update' : 'Sent Invite' }}
      </span>
    </button>
  </footer>

  <ng-template #loader>
    <tui-loader *ngIf="isCheckingExistence()" [showLoader]="true" [inheritColor]="true" size="s"
      class="tui-space_right-2"></tui-loader>
  </ng-template>
</tui-drawer>